/**
 * useReportStatus Hook - Status e Download do Relatorio
 * @module features/results/hooks/useReportStatus
 *
 * Hook para gerenciar o status do relatorio e realizar download.
 * Implementa polling automatico quando relatorio esta sendo gerado.
 *
 * T001-FE: Tela de Resumo com Resultados do Assessment
 * FIX-004: Corrigido para tratar 404 como 'generating' e continuar polling
 *
 * Comportamento:
 * - 404: status 'generating' (relatorio ainda sendo gerado, continuar polling)
 * - Apos MAX_POLLING_ATTEMPTS sem sucesso: status 'unavailable'
 * - Outros erros HTTP: status 'error'
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReportMetadata, ReportStatus, ScoresData, UseReportStatusReturn } from '../types';
import { MAX_POLLING_ATTEMPTS, POLLING_INTERVAL_MS } from '../types';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * URL base da API
 */
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Resposta do endpoint de status do relatorio
 */
interface ReportStatusResponse {
  status: string;
  reportUrl?: string;
  metadata?: ReportMetadata;
  scores?: ScoresData;
  insight?: string;
  error?: string;
  pdfReady?: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook para gerenciar status e download do relatorio
 *
 * @param sessionId - ID da sessao do assessment
 * @returns Estado do relatorio e funcoes de controle
 *
 * @example
 * ```tsx
 * const { status, reportUrl, download, retry, isReady } = useReportStatus('session-123');
 *
 * if (isReady) {
 *   return <button onClick={download}>Baixar Relatorio</button>;
 * }
 * ```
 */
export function useReportStatus(sessionId: string): UseReportStatusReturn {
  // State
  const [status, setStatus] = useState<ReportStatus>('loading');
  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<ReportMetadata | null>(null);
  const [scores, setScores] = useState<ScoresData | null>(null);
  const [insight, setInsight] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [pdfReady, setPdfReady] = useState(false);

  // Refs for cleanup
  const pollingAttempts = useRef(0);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  // ---------------------------------------------------------------------------
  // Fetch Status
  // ---------------------------------------------------------------------------

  /**
   * Busca o status atual do relatorio
   */
  const fetchStatus = useCallback(async () => {
    // Validate sessionId
    if (!sessionId) {
      setStatus('error');
      setError('ID da sessao nao fornecido');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/report/${sessionId}/status`);

      if (!isMountedRef.current) return;

      // FIX-004: Handle 404 as 'generating' - report may still be processing
      // Continue polling until MAX_POLLING_ATTEMPTS, then show 'unavailable'
      if (response.status === 404) {
        pollingAttempts.current++;

        if (pollingAttempts.current < MAX_POLLING_ATTEMPTS) {
          // Report not ready yet, show generating and continue polling
          setStatus('generating');
          setError(null);

          pollingTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              fetchStatus();
            }
          }, POLLING_INTERVAL_MS);
        } else {
          // Max attempts reached without finding report
          setStatus('unavailable');
          setError(null);
        }
        return;
      }

      // Handle other HTTP errors (real errors, not transient)
      if (!response.ok) {
        if (isMountedRef.current) {
          setStatus('error');
          setError(`HTTP ${response.status}: ${response.statusText}`);
        }
        return;
      }

      const data: ReportStatusResponse = await response.json();

      // Map API status to our status type
      const apiStatus = data.status.toLowerCase();

      if (apiStatus === 'ready' || apiStatus === 'completed') {
        setStatus('ready');
        setReportUrl(data.reportUrl || null);
        setMetadata(data.metadata || null);
        setScores(data.scores || null);
        setInsight(data.insight || null);
        setError(null);

        // FIX-026: Track PDF readiness separately - keep polling until PDF is ready
        const isPdfReady = data.pdfReady === true;
        setPdfReady(isPdfReady);

        if (!isPdfReady) {
          // PDF not ready yet, continue polling (but status is already 'ready' for preview)
          pollingAttempts.current++;
          if (pollingAttempts.current < MAX_POLLING_ATTEMPTS) {
            pollingTimeoutRef.current = setTimeout(() => {
              if (isMountedRef.current) {
                fetchStatus();
              }
            }, POLLING_INTERVAL_MS);
          } else {
            // Max attempts reached - enable download anyway (on-demand fallback)
            setPdfReady(true);
          }
        } else {
          pollingAttempts.current = 0;
        }
      } else if (
        apiStatus === 'generating' ||
        apiStatus === 'pending' ||
        apiStatus === 'not_found'
      ) {
        // FIX-004: Include 'not_found' as generating state
        setStatus('generating');
        setError(null);

        // Start/continue polling
        pollingAttempts.current++;

        if (pollingAttempts.current < MAX_POLLING_ATTEMPTS) {
          pollingTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              fetchStatus();
            }
          }, POLLING_INTERVAL_MS);
        } else {
          // Max attempts reached
          setStatus('unavailable');
          setError(null);
        }
      } else if (apiStatus === 'error' || apiStatus === 'failed') {
        setStatus('error');
        setError(data.error || 'Falha ao gerar relatorio');
      } else {
        // Unknown status - treat as generating and poll
        setStatus('generating');
        pollingAttempts.current++;

        if (pollingAttempts.current < MAX_POLLING_ATTEMPTS) {
          pollingTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              fetchStatus();
            }
          }, POLLING_INTERVAL_MS);
        }
      }
    } catch (err) {
      if (!isMountedRef.current) return;

      // FIX-004: On network errors, continue polling instead of failing immediately
      pollingAttempts.current++;

      if (pollingAttempts.current < MAX_POLLING_ATTEMPTS) {
        setStatus('generating');
        pollingTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            fetchStatus();
          }
        }, POLLING_INTERVAL_MS);
      } else {
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Erro ao verificar status do relatorio');
      }
    }
  }, [sessionId]);

  // ---------------------------------------------------------------------------
  // Retry
  // ---------------------------------------------------------------------------

  /**
   * Reinicia a verificacao de status
   */
  const retry = useCallback(() => {
    // Clear any existing polling
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }

    // Reset state
    pollingAttempts.current = 0;
    setStatus('loading');
    setError(null);
    setReportUrl(null);
    setMetadata(null);
    setScores(null);
    setInsight(null);
    setPdfReady(false);

    // Fetch status
    fetchStatus();
  }, [fetchStatus]);

  // ---------------------------------------------------------------------------
  // Download
  // ---------------------------------------------------------------------------

  /**
   * Faz download do relatorio
   */
  const download = useCallback(async () => {
    if (!reportUrl) {
      return;
    }

    setIsDownloading(true);

    try {
      const fullUrl = reportUrl.startsWith('http') ? reportUrl : `${API_URL}${reportUrl}`;
      const response = await fetch(fullUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      // Determine file extension from Content-Type (always accessible via CORS)
      const contentType = response.headers.get('Content-Type') || '';
      const isHtml = contentType.includes('text/html');
      const extension = isHtml ? 'html' : 'pdf';
      const dateStr = new Date().toISOString().split('T')[0];

      // Extract filename from Content-Disposition header or use fallback
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `relatorio-diagnostico-${dateStr}.${extension}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+?)"/);
        if (filenameMatch?.[1]) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Cleanup
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[useReportStatus] Download failed:', err);
      setError('Falha ao baixar relatorio. Tente novamente.');
    } finally {
      if (isMountedRef.current) {
        setIsDownloading(false);
      }
    }
  }, [reportUrl, sessionId]);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  // Initial fetch
  useEffect(() => {
    isMountedRef.current = true;
    fetchStatus();

    return () => {
      isMountedRef.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, [fetchStatus]);

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    status,
    reportUrl,
    error,
    metadata,
    scores,
    insight,
    isLoading: status === 'loading' || status === 'generating',
    isReady: status === 'ready',
    retry,
    download,
    isDownloading,
    pdfReady,
  };
}

export default useReportStatus;
